import React, { useEffect, useRef, useState } from 'react';
import { Text, Flex, Box, Separator, Tooltip } from "@radix-ui/themes";
import Icon from '@mdi/react';
import { mdiClose, mdiChartBar, mdiInformation } from '@mdi/js';
import { useStore } from '../store/store';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import * as d3 from 'd3';

// Register the annotation plugin
Chart.register(annotationPlugin);

// Scene type color mapping with updated types
const typeColors = {
  'action': '#FF5733', // Orange-red
  'dialogue-heavy': '#33A8FF', // Blue
  'exposition/worldbuilding': '#33FF57', // Green
  'suspense': '#FFFF33', // Yellow
  'horror/unease': '#A020F0', // Purple
  'emotional growth/reflection': '#FF33A8', // Pink
  'planning/strategy': '#33FFF5', // Cyan
  'discovery/revelation': '#FFA500', // Orange
  'travel/transition': '#8B4513', // Brown
  'climax/resolution': '#FF3333', // Red
  'unknown': '#CCCCCC' // Gray (default for unrecognized types)
};

// Scene type descriptions and examples for tooltips
const typeDescriptions = {
  "action": {
    "description": "High-intensity scenes with physical movement, combat, chases, or urgency.",
    "examples": ["Battles", "Fights", "Escapes", "Races against time"]
  },
  "dialogue-heavy": {
    "description": "Scenes where character conversations dominate.",
    "examples": ["Confessions", "Arguments", "Negotiations", "Interrogations"]
  },
  "exposition": {
    "description": "Scenes that provide background information, worldbuilding, or backstory.",
    "examples": ["Historical recounting", "Lore drops", "Info dumps via journals", "Character reflections"]
  },
  "suspense": {
    "description": "Scenes that build tension, introduce clues, or deepen an unknown element.",
    "examples": ["Investigations", "Characters uncovering hidden truths", "Cryptic warnings"]
  },
  "unease": {
    "description": "Scenes that evoke fear, dread, or psychological tension.",
    "examples": ["Supernatural events", "Eerie discoveries", "Slow-building paranoia"]
  },
  "emotional growth/reflection": {
    "description": "Focuses on a character's internal struggles, relationships, or emotional arcs.",
    "examples": ["Heartfelt confessions", "Personal realizations", "Grief", "Deep character introspection"]
  },
  "planning": {
    "description": "Characters devise plans, analyze situations, or prepare for future events.",
    "examples": ["War councils", "Heists", "Scientific experiments", "Puzzle-solving"]
  },
  "discovery": {
    "description": "A significant truth is revealed, altering the character's perception.",
    "examples": ["Identity reveals", "Shocking betrayals", "Scientific/magical breakthroughs"]
  },
  "transition": {
    "description": "Scenes that bridge locations or shift settings.",
    "examples": ["Journeys", "Setting transitions", "Travel montages", "World exploration"]
  }
};

// Format type name for display
const formatTypeName = (type) => {
  if (!type) return 'Unknown';
  
  // Handle special case for multiple types separated by comma
  if (type.includes(',')) {
    return type.split(',')
      .map(t => formatTypeName(t.trim()))
      .join(', ');
  }
  
  // Title case the type name
  return type.split('/').map(part => 
    part.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('-')
  ).join('/');
};

// Get the color for a scene type
const getTypeColor = (type) => {
  if (!type) return typeColors.unknown;
  
  // Handle multiple types separated by comma (use first type's color)
  if (type.includes(',')) {
    const firstType = type.split(',')[0].trim().toLowerCase();
    return typeColors[firstType] || typeColors.unknown;
  }
  
  return typeColors[type.toLowerCase()] || typeColors.unknown;
};

// Get description and examples for a scene type
const getTypeInfo = (type) => {
  if (!type) return null;
  
  // Handle multiple types separated by comma (use first type's info)
  if (type.includes(',')) {
    const firstType = type.split(',')[0].trim().toLowerCase();
    return typeDescriptions[firstType] || null;
  }
  
  return typeDescriptions[type.toLowerCase()] || null;
};

const SidebarAnalyticsScenes = ({ onClose }) => {
  const [state] = useStore();
  const novel = state.currentNovel;
  const chartRef = useRef(null);
  const dialogueChartRef = useRef(null);
  const typeChartRef = useRef(null);
  const chartInstance = useRef(null);
  const dialogueChartInstance = useRef(null);
  const [sceneTypes, setSceneTypes] = useState([]);
  
  // Calculate total scenes count
  const totalScenes = novel?.chapters?.reduce((count, chapter) => 
    count + (chapter.scenes?.length || 0), 0) || 0;
  
  // Process scenes to extract types from analysis
  useEffect(() => {
    if (!novel) return;
    
    const types = new Set();
    const scenesWithTypes = [];
    
    novel.chapters?.forEach((chapter, chapterIndex) => {
      chapter.scenes?.forEach((scene, sceneIndex) => {
        let type = 'unknown';
        let reason = ''; // Initialize reason as an empty string

        // Try to parse the analysis JSON if it exists
        if (scene.analysis) {
          try {
            const analysisData = JSON.parse(scene.analysis);
            type = analysisData.type || 'unknown';
            reason = analysisData.reason || ''; // Extract the reason field
            types.add(type.toLowerCase());
          } catch (error) {
            console.error('Error parsing scene analysis JSON:', error);
          }
        }

        scenesWithTypes.push({
          id: scene.id,
          chapter: chapter.title,
          chapterIndex,
          sceneIndex,
          type: type.toLowerCase(),
          reason, // Add reason to the scene object
          content: scene.content,
          words: scene.content ? scene.content.split(/\s+/).filter(Boolean).length : 0
        });
      });
    });
    
    setSceneTypes(Array.from(types));
    
    // Create the D3 visualization for scene types
    createSceneTypeVisualization(scenesWithTypes);
    
  }, [novel]);
  
  // D3 visualization for scene types
  const createSceneTypeVisualization = (scenes) => {
    // Wait for the DOM element to be available
    if (!typeChartRef.current) return;
    
    // Clear previous SVG
    d3.select(typeChartRef.current).selectAll('*').remove();
    
    // If no scenes, don't render
    if (!scenes || scenes.length === 0) return;
    
    // Group scenes by chapter
    const chapterScenes = d3.group(scenes, d => d.chapterIndex);
    
    // Chart dimensions - increased left margin to prevent "Chapter 1" from being cut off
    const margin = { top: 30, right: 20, bottom: 30, left: 80 }; // Reduced bottom margin since we're removing the legend
    const width = 820 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;
    
    // Create SVG container
    const svg = d3.select(typeChartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Calculate max scenes in any chapter
    const maxScenesInChapter = Math.max(...Array.from(chapterScenes.values(), scenes => scenes.length));
    
    // Calculate box size and spacing
    const boxSize = Math.min(22, (width / (chapterScenes.size + 1)) * 0.8);
    const boxSpacing = 1; // Reduced spacing between boxes
    
    // Y scale for chapters
    const yScale = d3.scaleLinear()
      .domain([0, chapterScenes.size - 1])
      .range([0, height]);
    
    // X scale for scenes within a chapter - modified to create less spacing
    const xScale = d3.scaleLinear()
      .domain([0, maxScenesInChapter - 1])
      .range([0, Math.min(width - 100, maxScenesInChapter * (boxSize + boxSpacing))]); 
    
    // Chapter labels - moved them slightly more to the left for clearer visibility
    svg.selectAll('.chapter-label')
      .data(Array.from(chapterScenes.keys()))
      .enter()
      .append('text')
      .attr('class', 'chapter-label')
      .attr('x', -15) // Increased space from -10 to -15
      .attr('y', d => yScale(d) + boxSize / 2 + 5)
      .attr('text-anchor', 'end')
      .attr('fill', 'white')
      .style('font-size', '12px')
      .text(d => `Chapter ${d + 1}`);
    
    // Create group for each chapter
    const chapters = svg.selectAll('.chapter')
      .data(Array.from(chapterScenes.entries()))
      .enter()
      .append('g')
      .attr('class', 'chapter')
      .attr('transform', d => `translate(0, ${yScale(d[0])})`);
    
    // Add scene boxes for each chapter
    const sceneBoxes = chapters.selectAll('.scene')
      .data(d => d[1])
      .enter()
      .append('rect')
      .attr('class', 'scene')
      .attr('x', d => xScale(d.sceneIndex) + (boxSpacing * d.sceneIndex)) // Add minimal spacing between boxes
      .attr('y', 0)
      .attr('width', 0) // Start with zero width for animation
      .attr('height', boxSize)
      .attr('rx', 3) // Rounded corners
      .attr('ry', 3)
      .attr('fill', d => getTypeColor(d.type))
      .attr('stroke', '#333333')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('y', -3)
          .attr('height', boxSize + 6)
          .attr('filter', 'url(#shadow)');
          
        // Get type info for enhanced tooltip
        const typeInfo = getTypeInfo(d.type);
        
        // Get exact position of the box for positioning tooltip directly above it
        const boxElement = d3.select(this).node();
        const boxRect = boxElement.getBoundingClientRect();
        
        // Position tooltip directly above the box
        const tooltipX = boxRect.left + (boxRect.width / 2);
        const tooltipY = boxRect.top - 10; // Position just above the box with a small gap
        
        // Show enhanced tooltip with type description and examples
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(`
          <strong>Chapter ${d.chapterIndex + 1}, Scene ${d.sceneIndex + 1}</strong><br/>
          <strong>Type:</strong> ${formatTypeName(d.type)}<br/>
          <strong>Reason:</strong> ${d.reason}<br/>
          <strong>Words:</strong> ${d.words}
        `)
          .style('left', tooltipX + 'px')
          .style('top', tooltipY + 'px')
          .style('max-width', '300px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('y', 0)
          .attr('height', boxSize)
          .attr('filter', null);
          
        // Hide tooltip
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
    
    // Create a filter for the shadow effect
    const defs = svg.append('defs');
    const filter = defs.append('filter')
      .attr('id', 'shadow')
      .attr('x', '-20%')
      .attr('y', '-20%')
      .attr('width', '140%')
      .attr('height', '140%');
      
    filter.append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '0')
      .attr('stdDeviation', '3')
      .attr('flood-color', '#000000')
      .attr('flood-opacity', '0.5');
    
    // Add animation to scene boxes - updated to match new positioning
    sceneBoxes.transition()
      .duration(500)
      .delay((d, i) => i * 30)
      .attr('width', boxSize)
      .attr('x', d => xScale(d.sceneIndex) + (boxSpacing * d.sceneIndex)); // Match the x position calculation
    
    // Create tooltip div with improved styling and positioning
    const tooltip = d3.select(typeChartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'fixed') // Changed to fixed positioning for more precise placement
      .style('padding', '10px')
      .style('background', '#3c4748')
      .style('border-radius', '5px')
      .style('color', 'white')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('z-index', 1000)
      .style('box-shadow', '0 4px 8px rgba(0,0,0,0.3)')
      .style('line-height', '1.4')
      .style('transform', 'translate(-50%, -100%)') // Center horizontally and position above target
      .style('margin-top', '-5px'); // Small offset
    
    // Legend code removed
  };
  
  // Original chart code
  // Process scene data for the word count chart
  useEffect(() => {
    if (!novel || !chartRef.current) return;
    
    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    
    // Collect all scenes with their word counts
    const allScenes = [];
    let totalWords = 0;
    let scenesWithWords = 0;
    
    novel.chapters?.forEach((chapter, chapterIndex) => {
      chapter.scenes?.forEach((scene, sceneIndex) => {
        // Calculate words for this scene if not already present
        const words = scene.words || 
          (scene.content ? scene.content.split(/\s+/).filter(Boolean).length : 0);
        
        if (words > 0) {
          totalWords += words;
          scenesWithWords++;
        }
        
        allScenes.push({
          id: scene.id,
          chapterIndex,
          chapterTitle: chapter.title,
          sceneIndex,
          words,
          label: `Ch ${chapterIndex + 1}, Scene ${sceneIndex + 1}`,
          dialogeStats: scene.dialogeStats || null
        });
      });
    });
    
    // Filter to only scenes with words and sort by chapter/scene order
    const scenesWithContent = allScenes.filter(scene => scene.words > 0);
    
    // Calculate average words per scene
    const averageWords = scenesWithWords > 0 ? Math.round(totalWords / scenesWithWords) : 0;
    
    // Prepare chart data
    const labels = scenesWithContent.map(scene => scene.label);
    const data = scenesWithContent.map(scene => scene.words);
    
    // Create gradient for bars
    const ctx = chartRef.current.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(75, 192, 192, 0.8)');
    gradient.addColorStop(1, 'rgba(75, 192, 192, 0.2)');
    
    // Create the chart
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Word Count',
            data,
            backgroundColor: gradient,
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Word Count',
              color: 'rgba(255, 255, 255, 0.8)'
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          },
          x: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        plugins: {
          legend: {
            labels: {
              color: 'rgba(255, 255, 255, 0.8)'
            }
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                const sceneIndex = context[0].dataIndex;
                const scene = scenesWithContent[sceneIndex];
                return `${scene.chapterTitle} - Scene ${scene.sceneIndex + 1}`;
              }
            }
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                yMin: averageWords,
                yMax: averageWords,
                borderColor: 'rgba(255, 255, 255, 0.7)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                  content: `Average: ${averageWords} words`,
                  enabled: true,
                  position: 'end',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  font: {
                    size: 12
                  },
                  padding: 6
                }
              }
            }
          }
        }
      }
    });
    
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [novel]);
  
  // Process dialogue ratio data for the second chart
  useEffect(() => {
    if (!novel || !dialogueChartRef.current) return;
    
    // Destroy previous chart if it exists
    if (dialogueChartInstance.current) {
      dialogueChartInstance.current.destroy();
    }
    
    // Collect all scenes with dialogue stats
    const allScenes = [];
    
    novel.chapters?.forEach((chapter, chapterIndex) => {
      chapter.scenes?.forEach((scene, sceneIndex) => {
        if (scene.dialogeStats && scene.words && scene.words > 0) {
          allScenes.push({
            id: scene.id,
            chapterIndex,
            chapterTitle: chapter.title,
            sceneIndex,
            dialogeStats: scene.dialogeStats,
            words: scene.words,
            label: `Ch ${chapterIndex + 1}, Scene ${sceneIndex + 1}`
          });
        }
      });
    });
    
    // If no scenes with dialogue stats, don't create a chart
    if (allScenes.length === 0) {
      return;
    }
    
    // Process dialogue stats data for the chart - using percentages instead of word counts
    const labels = allScenes.map(scene => scene.label);
    // Parse percentages from strings (removing the '%' character)
    const dialogueData = allScenes.map(scene => parseFloat(scene.dialogeStats.dialoguePercentage));
    const nonDialogueData = allScenes.map(scene => parseFloat(scene.dialogeStats.nonDialoguePercentage));
    
    // Create the stacked bar chart to show dialogue and non-dialogue distribution
    dialogueChartInstance.current = new Chart(dialogueChartRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Dialogue',
            data: dialogueData,
            backgroundColor: 'rgba(255, 173, 51, 0.8)',
            borderColor: 'rgba(255, 173, 51, 1)',
            borderWidth: 1
          },
          {
            label: 'Narrative',
            data: nonDialogueData,
            backgroundColor: 'rgba(75, 101, 192, 0.8)',
            borderColor: 'rgba(75, 101, 192, 1)',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            stacked: true,
            max: 100, // Set max to 100 since we're using percentages
            title: {
              display: true,
              text: 'Percentage',
              color: 'rgba(255, 255, 255, 0.8)'
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              callback: function(value) {
                return value + '%'; // Add percent sign to tick labels
              }
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          },
          x: {
            stacked: true,
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        plugins: {
          legend: {
            labels: {
              color: 'rgba(255, 255, 255, 0.8)'
            }
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                const sceneIndex = context[0].dataIndex;
                const scene = allScenes[sceneIndex];
                return `${scene.chapterTitle} - Scene ${scene.sceneIndex + 1}`;
              },
              label: function(context) {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                return `${label}: ${value.toFixed(1)}%`;
              },
              afterLabel: function(context) {
                const sceneIndex = context.dataIndex;
                const scene = allScenes[sceneIndex];
                const wordCount = context.datasetIndex === 0 
                  ? scene.dialogeStats.dialogueWords 
                  : scene.dialogeStats.nonDialogueWords;
                return `(${wordCount} words)`;
              }
            }
          }
        }
      }
    });
    
    return () => {
      if (dialogueChartInstance.current) {
        dialogueChartInstance.current.destroy();
      }
    };
  }, [novel]);
  
  return (
    <div className='sidebar-sidebar analytics-sidebar sidebar-analytics-scenes'>
      <div style={{textAlign: 'right', cursor: 'pointer'}} onClick={onClose}>
        <Icon path={mdiClose} color={'#FFFFFF50'} size={1} />
      </div>
      
      <Flex direction="column" gap="3">
        <Text size="4" weight="bold" style={{color: 'white'}}>
          Scene Analysis
        </Text>
        
        <Box p="3" style={{backgroundColor: '#3c4748', borderRadius: '8px'}}>
          <Text size="2" style={{color: 'white', opacity: 0.9}}>
            {novel?.title} contains {totalScenes} scenes across {novel?.chapters?.length || 0} chapters.
          </Text>
          
          <Box mt="4" style={{height: '300px', position: 'relative'}}>
            <canvas ref={chartRef}></canvas>
          </Box>
          
          <Text size="2" style={{color: 'white', opacity: 0.8, marginTop: '10px', fontStyle: 'italic', textAlign: 'center'}}>
            Word count distribution across scenes with written content
          </Text>
        </Box>
        
        <Box p="3" style={{backgroundColor: '#3c4748', borderRadius: '8px'}}>
          <Text size="3" weight="bold" style={{color: 'white', marginBottom: '10px'}}>
            Dialogue vs. Narrative
          </Text>
          
          <Box style={{height: '300px', position: 'relative'}}>
            <canvas ref={dialogueChartRef}></canvas>
          </Box>
          
          <Text size="2" style={{color: 'white', opacity: 0.8, marginTop: '10px', fontStyle: 'italic', textAlign: 'center'}}>
            Distribution of dialogue and narrative prose across scenes
          </Text>
        </Box>

        <Box p="3" style={{backgroundColor: '#3c4748', borderRadius: '8px'}}>
          <Flex justify="between" align="center" mb="2">
            <Text size="3" weight="bold" style={{color: 'white'}}>
              Scene Types Visualization
            </Text>
            <Tooltip content="Each box represents a scene, colored by its type">
              <div style={{cursor: 'help'}}>
                <Icon path={mdiInformation} size={0.8} color="white" />
              </div>
            </Tooltip>
          </Flex>
          
          <div ref={typeChartRef} style={{height: '350px', width: '100%'}}></div>
          
          <Text size="2" style={{color: 'white', opacity: 0.8, marginTop: '10px', fontStyle: 'italic', textAlign: 'center'}}>
            Visual map of scene types across chapters
          </Text>
        </Box>
        
        <Box p="3" style={{backgroundColor: '#2c3839', borderRadius: '8px', marginTop: '10px'}}>
          <Text size="3" weight="bold" style={{color: 'white', marginBottom: '10px'}}>
            Scene Analysis Insights
          </Text>
          <ul style={{color: 'white', opacity: 0.8, paddingLeft: '20px'}}>
            <li>Scene types help identify narrative flow and pacing throughout your novel</li>
            <li>Balance action scenes with emotional and character development moments</li>
            <li>Look for patterns in your scene types - do you favor certain types over others?</li>
            <li>Consider where exposition scenes are placed - too many in sequence may slow pacing</li>
            <li>Transition scenes help readers follow location changes but shouldn't dominate</li>
            <li>Dialogue-heavy scenes create character relationships and reveal information</li>
          </ul>
        </Box>
      </Flex>
      
      <style jsx>{`
        .sidebar-analytics-scenes {
          width: 900px !important;
        }
      `}</style>
    </div>
  );
};

export default SidebarAnalyticsScenes;
